import { AnchorButton, Button, Card, SkeletonLoader } from '@payaca/ambrosia';
import { currencyPrice } from '@payaca/helpers/financeHelper';
import { getAccountRegionFromCurrencyCodeLegacy } from '@payaca/helpers/internationalHelper';
import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import UntitledIcon, { TIconName } from '@payaca/untitled-icons';
import { Nullish } from '@payaca/utilities/types';
import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useGetUser } from '../../../../api/queries/user/useGetUser';
import { PermissionGuard } from '../../../components/permissionGuard/PermissionGuard';
import { UpdateUserModal } from '../../../components/updateUserModal/UpdateUserModal';
import UserTeamsCard from './UserTeamsCard';

const UserProfile: FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const {
    data: userData,
    refetch: refetchUser,
    isLoading,
  } = useGetUser(+userId);
  const user = userData?.user;
  const teams = user?.teams.items ?? [];

  const region =
    user?.costPerHour?.currency &&
    getAccountRegionFromCurrencyCodeLegacy(
      user?.costPerHour?.currency.code || 'gbp'
    );

  const [showUpdateUserProfileModal, setShowUpdateUserProfileModal] =
    useState(false);

  return (
    <>
      <div className="grid gap-4 md:grid-cols-3">
        <div className="md:col-span-2">
          <Card>
            <Card.Body>
              <div className="flex items-center justify-between">
                <h3>About</h3>
                {!isLoading && (
                  <PermissionGuard
                    renderIfHasPermissions={[
                      AccountsPermissions.SET_USER_COLOUR,
                      AccountsPermissions.SET_USER_COST_PER_HOUR,
                      AccountsPermissions.UPDATE_ROLE,
                    ]}
                  >
                    <Button
                      size="sm"
                      onClick={() => setShowUpdateUserProfileModal(true)}
                      variant="secondary"
                    >
                      Edit
                    </Button>
                  </PermissionGuard>
                )}
              </div>
              {isLoading ? (
                <AboutSkeleton />
              ) : (
                <div className="mt-4 flex flex-col gap-y-3">
                  <UserProperty
                    iconName="key-01.3"
                    label="Role"
                    value={user?.systemRole}
                  />
                  <PermissionGuard
                    renderIfHasPermissions={[
                      AccountsPermissions.GET_USER_COST_PER_HOUR,
                    ]}
                  >
                    <UserProperty
                      iconName="coins-stacked-02.3"
                      label="Cost per hour"
                      value={
                        user?.costPerHour?.value != null
                          ? currencyPrice(user.costPerHour.value, region)
                          : undefined
                      }
                    />
                  </PermissionGuard>
                  <UserProperty
                    iconName="mail-02.3"
                    label="Email"
                    value={user?.email}
                    href={user?.email ? `mailto:${user.email}` : undefined}
                  />
                  <UserProperty
                    iconName="phone.3"
                    label="Phone"
                    value={user?.phone}
                  />
                </div>
              )}
            </Card.Body>
          </Card>
        </div>

        <div className="self-start">
          {isLoading ? (
            <TeamsCardSkeleton />
          ) : (
            user && (
              <UserTeamsCard
                userPublicId={user.publicId}
                teams={teams}
                onChanged={() => void refetchUser()}
              />
            )
          )}
        </div>
      </div>
      <UpdateUserModal
        userId={+userId}
        isOpen={showUpdateUserProfileModal}
        onClose={() => setShowUpdateUserProfileModal(false)}
        onSuccess={() => {
          void refetchUser();
          setShowUpdateUserProfileModal(false);
        }}
      />
    </>
  );
};

const AboutSkeleton: FC = () => (
  <div className="mt-4 flex flex-col gap-y-3">
    {['role', 'cost', 'email', 'phone'].map((key) => (
      <div key={key} className="flex items-center gap-x-3">
        <SkeletonLoader className="h-4 w-4 shrink-0" />
        <SkeletonLoader.Text className="w-28 shrink-0" />
        <SkeletonLoader.Text className="w-40" />
      </div>
    ))}
  </div>
);

const TeamsCardSkeleton: FC = () => (
  <Card>
    <Card.Body>
      <h3>Teams</h3>
      <div className="mt-3 flex flex-wrap gap-2">
        <SkeletonLoader.Badge />
        <SkeletonLoader.Badge />
      </div>
    </Card.Body>
  </Card>
);

type UserPropertyProps = {
  iconName: TIconName;
  label: string;
  value?: Nullish<string | number>;
  href?: string;
};
const UserProperty: FC<UserPropertyProps> = ({
  iconName,
  label,
  value,
  href,
}) => {
  if (!value) {
    return null;
  }
  return (
    <div className="flex items-center gap-x-3">
      <UntitledIcon
        name={iconName}
        className="h-4 w-4 shrink-0 text-gray-500"
      />
      <span className="w-28 shrink-0 text-gray-500">{label}</span>
      {href ? (
        <AnchorButton href={href} variant="link">
          {value}
        </AnchorButton>
      ) : (
        <span className="font-medium">{value}</span>
      )}
    </div>
  );
};

export default UserProfile;
